/**
 * Сведения о клиенте для журнала входов и выходов API (заголовки X-Client*).
 *
 * Сервер пишет их в журнал только для диагностики: по версии видно, какая
 * сборка SPA была у пользователя, по идентификатору вкладки — какая из
 * вкладок одного браузера обменяла токен, а какая упёрлась в чёрный список.
 * Значение не по формату сервер молча игнорирует, поэтому здесь форматы те же,
 * что в OpenAPI (`components/parameters/XClient*`).
 */

export const CLIENT_NAME = 'spa'

const VERSION_PATTERN = /^[A-Za-z0-9._+-]{1,64}$/
const TAB_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/
const TAB_ID_STORAGE_KEY = 'easya_tab_id'

/**
 * SHA сборки: `VITE_APP_VERSION` выставляет job `build` в `.gitlab-ci.yml`.
 * Локальная сборка и dev-сервер переменную не получают — тогда `dev`.
 */
export const CLIENT_VERSION = resolveClientVersion(import.meta.env.VITE_APP_VERSION)

function resolveClientVersion(raw: string | undefined): string {
  const value = raw?.trim() ?? ''

  return VERSION_PATTERN.test(value) ? value : 'dev'
}

let tabId: string | null = null

function generateTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  // randomUUID есть только в защищённом контексте (https, localhost); стенд
  // по http в локальной сети его не получит, а getRandomValues доступен везде.
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)

  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function readSessionTabId(): string | null {
  try {
    const value = sessionStorage.getItem(TAB_ID_STORAGE_KEY)
    sessionStorage.removeItem(TAB_ID_STORAGE_KEY)

    return value !== null && TAB_ID_PATTERN.test(value) ? value : null
  } catch {
    return null
  }
}

function writeSessionTabId(value: string): void {
  try {
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, value)
  } catch {
    // sessionStorage недоступен — идентификатор живёт до перезагрузки вкладки
  }
}

function removeSessionTabId(): void {
  try {
    sessionStorage.removeItem(TAB_ID_STORAGE_KEY)
  } catch {
    // см. writeSessionTabId
  }
}

/**
 * Идентификатор вкладки: переживает перезагрузку, но не копируется в дубликат.
 *
 * Браузер копирует sessionStorage в дубликат вкладки, и два окна получили бы
 * один идентификатор. Поэтому, пока страница открыта, в sessionStorage его нет:
 * он читается и сразу удаляется, а возвращается туда только на `pagehide` —
 * перед перезагрузкой или уходом со страницы.
 */
export function getTabId(): string {
  if (tabId !== null) {
    return tabId
  }

  const id = readSessionTabId() ?? generateTabId()
  tabId = id

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => writeSessionTabId(id))
    window.addEventListener('pageshow', (event) => {
      // Страница вернулась из bfcache: снова живая — убрать, как при загрузке
      if (event.persisted) {
        removeSessionTabId()
      }
    })
  }

  return id
}

/** Заголовки клиента для запросов к API */
export function clientHeaders(): Record<string, string> {
  return {
    'X-Client': CLIENT_NAME,
    'X-Client-Version': CLIENT_VERSION,
    'X-Client-Tab': getTabId(),
  }
}
